// src/components/home/VibeToolsPreview.jsx - Teen-Friendly Interactive Tools Preview
import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Zap, Star, Heart, Search, Shield, Crown, 
  Brain, Target, Headphones, Users, Sparkles, Trophy,
  PlayCircle, Lock, Gamepad2,
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';

const VibeToolsPreview = () => {
  const { setCurrentView } = useApp();
  
  // Featured tools for Vibe Edition - teen-friendly descriptions
  const vibeTools = [
    {
      id: 'inner-dj-controller',
      title: 'Inner DJ Controller',
      subtitle: 'Take Control of Your Brain\'s Playlist',
      description: 'Ever notice your brain plays the same negative songs on repeat? Time to become your own inner DJ and curate a soundtrack that actually serves you.',
      icon: Headphones,
      color: 'from-purple-500 to-indigo-500',
      badge: 'Mind Hacking',
      emoji: '🎧',
      vibeCheck: 'What\'s your brain playing right now?',
      features: [
        'Spot toxic thought patterns instantly',
        'Create your personal power playlist',
        'Master the art of mental DJing',
      ],
      coolFactor: 'Like Spotify for your thoughts',
    },
    {
      id: 'thought-detective-kit',
      title: 'Thought Detective Kit',
      subtitle: 'Fact-Check Your Brain Like a Pro',
      description: 'Your brain loves creating fake news about your life. Time to become a detective and separate the facts from the fiction your mind makes up.',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      badge: 'Reality Check',
      emoji: '🕵️‍♀️',
      vibeCheck: 'Is this thought actually true or just brain drama?',
      features: [
        'Spot cognitive distortions instantly',
        'Build an evidence-based mindset',
        'Become immune to mental fake news',
      ],
      coolFactor: 'CSI but for your thoughts',
    },
    {
      id: 'emotion-surfing-simulator',
      title: 'Emotion Surfing Academy',
      subtitle: 'Ride the Waves Instead of Drowning',
      description: 'Emotions are like ocean waves - you can fight them and get wiped out, or learn to surf them like a pro. Guess which one\'s more fun?',
      icon: Heart,
      color: 'from-teal-500 to-green-500',
      badge: 'Wave Rider',
      emoji: '🌊',
      vibeCheck: 'Ready to surf instead of struggle?',
      features: [
        'Master the 90-second emotion rule',
        'Turn breakdowns into breakthroughs',
        'Surf anxiety like it\'s no big deal',
      ],
      coolFactor: 'Emotional intelligence meets extreme sports',
    },
    {
      id: 'social-confidence-arcade',
      title: 'Social Confidence Arcade',
      subtitle: 'Level Up Your People Skills',
      description: 'Build genuine confidence that doesn\'t depend on everyone liking you. Practice authenticity, master conversations, and become magnetic.',
      icon: Users,
      color: 'from-pink-500 to-rose-500',
      badge: 'Social Skills',
      emoji: '🎮',
      vibeCheck: 'Ready to be authentically magnetic?',
      features: [
        'Practice conversations in safe scenarios',
        'Master the art of authentic vulnerability',
        'Build confidence that actually lasts',
      ],
      coolFactor: 'Like a dating sim but for real life confidence',
    },
    {
      id: 'future-self-architect',
      title: 'Future Self Architect',
      subtitle: 'Design Your Life Like Your Favourite Character',
      description: 'Stop living your life on autopilot. Design your future self like you\'re creating your favourite character in your favourite game.',
      icon: Crown,
      color: 'from-yellow-500 to-orange-500',
      badge: 'Life Design',
      emoji: '👑',
      vibeCheck: 'Who is your future self going to be?',
      features: [
        'Vision board on steroids',
        'Identity architecture system',
        'Turn dreams into detailed blueprints',
      ],
      coolFactor: 'Character creation but for your actual life',
    },
    {
      id: 'resilience-training',
      title: 'Phoenix Rising Simulator',
      subtitle: 'Turn Setbacks into Comebacks',
      description: 'Learn to bounce back from anything life throws at you. Not just surviving the tough times, but coming back stronger every single time.',
      icon: Trophy,
      color: 'from-red-500 to-orange-500',
      badge: 'Antifragility',
      emoji: '🔥',
      vibeCheck: 'Ready to become unbreakable?',
      features: [
        'Build antifragile mental resilience',
        'Transform failures into fuel',
        'Master the art of the comeback',
      ],
      coolFactor: 'Like a phoenix, but cooler',
    },
  ];
  
  return (
    <section className="py-20 bg-gradient-to-br from-slate-800/50 to-purple-900/20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Gamepad2 className="w-6 h-6 text-yellow-400" />
            <span className="text-yellow-400 font-medium">Interactive Tools That Hit Different</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Tools That Help Transform You
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Go beyond learning with interactive tools that let you practice, experiment, and master your inner voice. 
            <span className="text-purple-300 font-semibold"> Think of them as training simulators for your mind.</span>
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {vibeTools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="h-full"
            >
              <Card className="p-6 h-full hover-lift bg-gradient-to-br from-white/5 to-white/10 border-purple-500/20 relative overflow-hidden group">
                {/* Background glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                <div className="relative space-y-4 h-full flex flex-col">
                  {/* Header */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform relative`}>
                          <tool.icon className="w-8 h-8 text-white" />
                          <div className="absolute -top-2 -right-2 text-lg">
                            {tool.emoji}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                            tool.badge === 'Mind Hacking' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                            tool.badge === 'Reality Check' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                            tool.badge === 'Wave Rider' ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' :
                            tool.badge === 'Social Skills' ? 'bg-pink-500/20 text-pink-300 border-pink-500/30' :
                            tool.badge === 'Life Design' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                            'bg-red-500/20 text-red-300 border-red-500/30'
                          }`}>
                            {tool.badge}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-purple-200 transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-purple-200 font-medium">
                        {tool.subtitle}
                      </p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 space-y-4">
                    <p className="text-slate-300 leading-relaxed">
                      {tool.description}
                    </p>
                    
                    {/* Vibe Check */}
                    <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <p className="text-purple-300 text-sm font-medium">
                        💭 {tool.vibeCheck}
                      </p>
                    </div>
                    
                    {/* Cool Factor */}
                    <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <p className="text-yellow-300 text-sm font-medium">
                        ✨ {tool.coolFactor}
                      </p>
                    </div>
                    
                    {/* Features */}
                    <div>
                      <h4 className="text-white font-medium mb-2 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-400" />
                        What You'll Master:
                      </h4>
                      <div className="space-y-1">
                        {tool.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                            <Star className="w-3 h-3 text-purple-400" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="pt-4 border-t border-white/10">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="w-full text-purple-300 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setCurrentView('tools')}
                    >
                      <PlayCircle className="w-3 h-3 mr-1" />
                      Try This Tool
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
        
        {/* Unlock Message for Guests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mb-12"
        >
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lock className="w-6 h-6 text-yellow-400" />
              <h3 className="text-2xl font-bold text-white">Unlock All 8 Interactive Tools</h3>
            </div>
            <p className="text-slate-300 mb-6">
              These aren't just exercises - they're like training simulators for your mind. Practice, experiment, 
              and master your inner voice in a safe space before applying it to real life.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-white/5 rounded-lg">
                <Brain className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <div className="text-white font-medium">Practice Safe</div>
                <div className="text-slate-400 text-sm">No judgment, just growth</div>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className="text-white font-medium">Get Instant Feedback</div>
                <div className="text-slate-400 text-sm">See your progress in real-time</div>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <div className="text-white font-medium">Master Your Mind</div>
                <div className="text-slate-400 text-sm">Build skills that last forever</div>
              </div>
            </div>
            <Button 
              onClick={() => setCurrentView('vibe-assessment-intro')} 
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Crown className="w-5 h-5 mr-2" />
              Start Free Vibe Check to Unlock
            </Button>
            <p className="text-slate-500 text-sm mt-3">
              Take the assessment → See your results → Unlock your personalised tool kit
            </p>
          </div>
        </motion.div>
        
        {/* Quick Benefits Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
          className="grid md:grid-cols-4 gap-6 text-center"
        >
          {[
            { icon: Heart, title: 'Practice Emotions', desc: 'Surf feelings like a pro', color: 'text-pink-400' },
            { icon: Brain, title: 'Train Your Mind', desc: 'Become your inner DJ', color: 'text-purple-400' },
            { icon: Users, title: 'Social Skills', desc: "Confidence that's authentic", color: 'text-blue-400' },
            { icon: Crown, title: 'Design Your Future', desc: 'Create your dream life', color: 'text-yellow-400' },
          ].map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1.1 + index * 0.1 }}
              className="p-4"
            >
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-3">
                <benefit.icon className={`w-6 h-6 ${benefit.color}`} />
              </div>
              <h4 className="text-white font-medium mb-2">{benefit.title}</h4>
              <p className="text-slate-400 text-sm">{benefit.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.3 }}
          className="text-center mt-12"
        >
          <div className="bg-gradient-to-r from-slate-800/50 to-purple-900/30 rounded-2xl p-6 max-w-2xl mx-auto border border-purple-500/20">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Ready to Level Up Your Mind Game?
            </h4>
            <p className="text-slate-300 mb-4">
              These tools turn abstract concepts into hands-on practice. It's like having a gym membership for your brain.
            </p>
            <Button 
              onClick={() => setCurrentView('tools')} 
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              Explore All Tools
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeToolsPreview;